import * as React from 'react';
import { useDropzone } from 'react-dropzone';

import { useApp } from '@src/hooks/useApp';
import { useFirebase } from '@src/hooks/useFirebase';
import { useSurvey } from '@src/hooks/useSurvey';
import { SurveyQuestionProps } from './SurveyQuestion';

export interface TypeImageProps extends SurveyQuestionProps {}

/**
 * @name TypeImage
 * @description A form that will upload to our Firebase storage which has
 * plugins installed that will optimize and resize our uploaded images
 */
export const TypeImage = (props: TypeImageProps) => {
  const { question } = props;

  // Hooks
  const refUploader = React.useRef<HTMLDivElement>(null);
  const { answers, setAnswer, setError } = useSurvey();
  const { toggleLoader } = useApp();
  const { uploadPhoto } = useFirebase();

  // Setup
  const maxFileSize = 10; // MB
  const value = answers[question.name];

  // Handlers
  const onClickUpload = () => {
    if (!refUploader.current) return;

    refUploader.current.click();
  };

  const onClickRemove = () => {
    setAnswer(question.name, undefined);
  };

  const onDropAccepted = async (files: File[]) => {
    setError(undefined);

    // We accept only one file max
    const file = files[0];
    const reader = new FileReader();

    reader.onabort = () => setError('File upload aborted!');
    reader.onerror = () => setError('File upload error!');
    reader.onload = async () => {
      toggleLoader(true);

      try {
        const url = await uploadPhoto(file);
        setAnswer(question.name, url);
      } catch (error) {
        const message = `File upload error, please try again.`;
        const msg = error instanceof Error ? error.message : message;
        setError(msg);
      }

      toggleLoader(false);
    };
    reader.readAsDataURL(file);
  };

  // Markup
  const renderPlaceholder = () => {
    return (
      <div className="text-gray-5">
        <div className="mt-2x">
          <img src="/images/svg/camera.svg" />
          <div className="text-14">optional</div>
        </div>
      </div>
    );
  };

  const renderRemoveButton = () => (
    <button
      className="mt-10 inline-flex rounded border-0 bg-white px-6 py-2 text-lg text-rust hover:cursor-pointer"
      onClick={onClickRemove}
      type="button"
    >
      Remove Photo
    </button>
  );

  // Life-Cycle/Hooks
  const { getInputProps, getRootProps } = useDropzone({
    accept: {
      'image/*': ['.png', '.gif', '.jpeg', '.jpg', '.webp']
    },
    maxFiles: 1,
    maxSize: maxFileSize * 1000000,
    multiple: false,
    onDropAccepted,
    onDropRejected: () => {
      setError(`Please select an image under ${maxFileSize} MB.`);
    }
  });

  return (
    <>
      <div className="relative m-auto flex h-56 w-56 items-center justify-center overflow-hidden rounded-full bg-white p-0 font-beatrice-light text-16 md:h-72 md:w-72 2xl:h-96 2xl:w-96 2xl:text-18">
        <div
          {...getRootProps({
            className: 'h-full w-full absolute',
            ref: refUploader
          })}
        >
          <input
            {...getInputProps({
              name: question.name,
              required: !!question.required
            })}
          />
        </div>

        <button className="m-0 p-0" onClick={onClickUpload} type="button">
          {value && <img className="h-full w-full" src={value} />}
          {!value && renderPlaceholder()}
        </button>
      </div>

      <div className="flex justify-center gap-4">
        {value && renderRemoveButton()}

        <button
          className="mt-10 inline-flex rounded border-0 bg-rust px-6 py-2 text-lg text-white hover:cursor-pointer"
          onClick={onClickUpload}
          type="button"
        >
          Upload photo
        </button>
      </div>
    </>
  );
};
